/**
 * ScoreReport — branded, screenshot-friendly placement-quiz result card.
 *
 * Features:
 *  - Cleveroo logo + name header
 *  - Big score %, recommended band, encouraging copy
 *  - Weak-area breakdown as labeled progress bars (falls back to focusArea tags)
 *  - Download as PNG  (html-to-image, captures only the card ref)
 *  - Share via Web Share API with PNG file when supported; clipboard+download fallback
 *  - Action buttons are OUTSIDE the card ref — excluded from the captured image
 */

import React, { useRef, useState, useCallback } from 'react';
import { toPng } from 'html-to-image';
import { PlacementResult } from '../api/placement';
import { track } from '../api/analytics';
import logoUrl from '../assets/images/cleveroo-logo.png';
import RadarChart from './reports/RadarChart';
import ReportInsights from './reports/ReportInsights';

// ─── Domain label map (matches PlacementQuiz.tsx) ────────────────────────────
const DOMAIN_LABEL: Record<string, string> = {
  numeracy: 'Maths',
  maths: 'Maths',
  mathematics: 'Maths',
  reading: 'Reading',
  language_conventions: 'Language',
  language: 'Language',
  writing: 'Writing',
};

function domainLabel(area: string): string {
  return DOMAIN_LABEL[area.toLowerCase()] ?? area;
}

// ─── Tone map for progress bar colours ───────────────────────────────────────
const TONE_COLORS: { bar: string; bg: string }[] = [
  { bar: '#E26B4F', bg: '#FAD5C8' }, // coral  – weakest (index 0)
  { bar: '#F58720', bg: '#FFE0BF' }, // orange
  { bar: '#F4C534', bg: '#FFF2BF' }, // yellow
  { bar: '#5DAA3A', bg: '#DCEFCC' }, // green
];

function toneFor(index: number): { bar: string; bg: string } {
  const clampedIdx = Math.min(index, TONE_COLORS.length - 1);
  return TONE_COLORS[clampedIdx];
}

// ─── Arrow icon ───────────────────────────────────────────────────────────────
const ArrowRight = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14m-6-6 6 6-6 6" />
  </svg>
);

// ─── Encouraging lines, keyed by score bracket ───────────────────────────────
function encouragingLine(scorePercent: number): string {
  if (scorePercent >= 80) return 'Outstanding — you\'re set for an ambitious learning path!';
  if (scorePercent >= 60) return 'Solid result — your personalised plan will sharpen the rest!';
  if (scorePercent >= 40) return 'Great start — every expert was once a beginner!';
  return 'Your journey begins here — we\'ve got a plan to boost every area!';
}

// ─── Props ────────────────────────────────────────────────────────────────────
export interface ScoreReportProps {
  result: PlacementResult;
  studentName?: string | null;
  onClose: () => void;
  /** Optional "Practise now" CTA — closes the loop into practice (ROO-132). */
  onPractise?: () => void;
  /** Optional per-skill "Practise this" on weak breakdown rows (ROO-133). */
  onPractiseSkill?: (area: string, label: string) => void;
  /** Band is a placement concept — hide it for warm-ups/check-ins (ROO-219). */
  showBand?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────
const ScoreReport: React.FC<ScoreReportProps> = ({ result, studentName, onClose, onPractise, onPractiseSkill, showBand = true }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [capturing, setCapturing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const scoreRounded = Math.round(result.scorePercent);
  const hasBreakdown = Array.isArray(result.breakdown) && result.breakdown.length > 0;
  const focusLabels = (result.focusAreas || []).map(domainLabel);

  // Radar axes: prefer skill-level breakdown (>=3), else fall back to domains (ROO-131).
  const radarAxes =
    hasBreakdown && result.breakdown!.length >= 3
      ? result.breakdown!.map((b) => ({ label: b.label || domainLabel(b.area), percent: b.percent }))
      : (result.perDomain || []).map((d) => ({ label: domainLabel(d.domain), percent: d.percent }));
  const reportDate = new Date().toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Generate PNG blob from the card (action buttons excluded — they live outside cardRef)
  const generatePng = useCallback(async (): Promise<Blob> => {
    if (!cardRef.current) throw new Error('Card ref not ready');
    setCapturing(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        backgroundColor: '#FFFBF0',
        // Exclude any elements marked with data-no-capture
        filter: (node) => {
          if (node instanceof HTMLElement && node.dataset['noCapture']) return false;
          return true;
        },
      });
      const res = await fetch(dataUrl);
      return await res.blob();
    } finally {
      setCapturing(false);
    }
  }, []);

  // ── Download action ──
  const handleDownload = useCallback(async () => {
    try {
      const blob = await generatePng();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cleveroo-score-report.png';
      a.click();
      URL.revokeObjectURL(url);
      void track('scoreReportShared', {
        properties: {
          action: 'download',
          scorePercent: result.scorePercent,
          recommendedBand: result.recommendedBand,
        },
      });
      showToast('Score report downloaded!');
    } catch {
      showToast('Could not generate image. Please try again.');
    }
  }, [generatePng, result.scorePercent, result.recommendedBand, showToast]);

  // ── Share action ──
  const handleShare = useCallback(async () => {
    const textSummary =
      `My Cleveroo Score Report\n` +
      `Score: ${scoreRounded}% (${result.correct}/${result.totalQuestions} correct)\n` +
      `Band: ${result.recommendedBand}\n` +
      `${encouragingLine(result.scorePercent)}\n` +
      `Try it at https://cleverooedu.com.au/?utm_source=score_report&utm_medium=share`;

    try {
      const blob = await generatePng();
      const file = new File([blob], 'cleveroo-score-report.png', { type: 'image/png' });

      if (
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          title: 'My Cleveroo Score Report',
          text: textSummary,
          files: [file],
        });
        void track('scoreReportShared', {
          properties: {
            action: 'webShare',
            scorePercent: result.scorePercent,
            recommendedBand: result.recommendedBand,
          },
        });
      } else {
        // Fallback: copy text to clipboard + download image
        await navigator.clipboard.writeText(textSummary);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'cleveroo-score-report.png';
        a.click();
        URL.revokeObjectURL(url);
        void track('scoreReportShared', {
          properties: {
            action: 'clipboardFallback',
            scorePercent: result.scorePercent,
            recommendedBand: result.recommendedBand,
          },
        });
        showToast('Link copied & image downloaded!');
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        showToast('Could not share. Please try again.');
      }
    }
  }, [generatePng, result, scoreRounded, showToast]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', marginTop: 24, marginBottom: 24 }}>
      {/* ── BRANDED CARD (captured for screenshot) ── */}
      <div
        ref={cardRef}
        style={{
          background: '#FFFBF0',
          border: '2px solid #EFE6D0',
          borderRadius: 24,
          padding: '32px 28px 24px',
          width: '100%',
          maxWidth: 540,
          fontFamily: "'Lexend', system-ui, sans-serif",
          WebkitFontSmoothing: 'antialiased',
          boxSizing: 'border-box',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 24,
            paddingBottom: 16,
            borderBottom: '1.5px solid #EFE6D0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img
              src={logoUrl}
              alt="Cleveroo"
              style={{ width: 36, height: 36, objectFit: 'contain' }}
            />
            <span
              style={{
                fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 22,
                color: '#14593C',
                letterSpacing: '-0.01em',
              }}
            >
              Cleve<span style={{ color: '#5DAA3A' }}>r</span>
              <span style={{ color: '#F58720' }}>oo</span>
            </span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#6B7A6F',
                textTransform: 'uppercase',
                letterSpacing: '0.10em',
              }}
            >
              Score Report
            </div>
            <div style={{ fontSize: 11, color: '#6B7A6F', marginTop: 2 }}>
              {reportDate}
            </div>
          </div>
        </div>

        {/* Student name (if available) */}
        {studentName && (
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: '#6B7A6F',
              marginBottom: 16,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {studentName}
          </div>
        )}

        {/* Big score + band row */}
        <div style={{ display: 'grid', gridTemplateColumns: showBand ? '1fr 1fr' : '1fr', gap: 14, marginBottom: 20 }}>
          {/* Score */}
          <div
            style={{
              background: 'white',
              border: '1.5px solid #EFE6D0',
              borderRadius: 16,
              padding: 20,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#6B7A6F',
                textTransform: 'uppercase',
                letterSpacing: '0.10em',
                marginBottom: 8,
              }}
            >
              Score
            </div>
            <div
              style={{
                fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 48,
                lineHeight: 1,
                color: '#14593C',
              }}
            >
              {scoreRounded}
              <span style={{ fontSize: 20 }}>%</span>
            </div>
            <div style={{ fontSize: 12, color: '#6B7A6F', marginTop: 6 }}>
              {result.correct} of {result.totalQuestions} correct
            </div>
          </div>

          {/* Band — placement only (ROO-219) */}
          {showBand && (
          <div
            style={{
              background: 'white',
              border: '1.5px solid #EFE6D0',
              borderRadius: 16,
              padding: 20,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#6B7A6F',
                textTransform: 'uppercase',
                letterSpacing: '0.10em',
                marginBottom: 8,
              }}
            >
              Recommended Band
            </div>
            <div
              style={{
                fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
                fontWeight: 800,
                fontSize: 48,
                lineHeight: 1,
                color: '#14593C',
              }}
            >
              {result.recommendedBand}
            </div>
            <div style={{ fontSize: 12, color: '#6B7A6F', marginTop: 6 }}>
              Your starting level
            </div>
          </div>
          )}
        </div>

        {/* "Since last time" comparison block (ROO-157) — only when this is a retake */}
        {(() => {
          if (!result.comparison) return null;
          const comp = result.comparison;
          const prevDateObj = comp.previousAt ? new Date(comp.previousAt) : null;
          const prevDate =
            prevDateObj && !isNaN(prevDateObj.getTime())
              ? prevDateObj.toLocaleDateString('en-AU', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : null;
          const overallDelta = comp.overall.delta;

          // Merge subjects + topics (non-null delta only), sort by biggest absolute mover, take top 6
          const allMovers = [
            ...comp.subjects
              .filter((s) => s.delta !== null)
              .map((s) => ({ key: s.subject, label: s.subjectLabel, delta: s.delta as number })),
            ...comp.topics
              .filter((t) => t.delta !== null)
              .map((t) => ({ key: t.area, label: t.label, delta: t.delta as number })),
          ].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)).slice(0, 6);
          const improved = allMovers.filter((m) => m.delta > 0);
          const declined = allMovers.filter((m) => m.delta < 0);

          return (
            <div
              style={{
                background: '#F5FBF0',
                border: '1.5px solid #C5E0B0',
                borderRadius: 14,
                padding: '14px 16px',
                marginBottom: 20,
              }}
            >
              {/* header row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 10,
                  flexWrap: 'wrap',
                  gap: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#14593C',
                    textTransform: 'uppercase',
                    letterSpacing: '0.10em',
                  }}
                >
                  Since last time
                </span>
                {prevDate && <span style={{ fontSize: 11, color: '#6B7A6F' }}>vs {prevDate}</span>}
              </div>

              {/* overall score row */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 6,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ fontSize: 13, color: '#1A2A1F', fontWeight: 600 }}>
                  {Math.round(comp.overall.previous)}% → {Math.round(comp.overall.current)}%
                </span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    padding: '2px 9px',
                    borderRadius: 999,
                    background:
                      overallDelta > 0 ? '#DCEFCC' : overallDelta < 0 ? '#FAD5C8' : '#F0EDE4',
                    color:
                      overallDelta > 0 ? '#14593C' : overallDelta < 0 ? '#B83E24' : '#6B7A6F',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {overallDelta > 0 ? '▲' : overallDelta < 0 ? '▼' : '='}
                  {' '}
                  {overallDelta > 0
                    ? `+${Math.round(overallDelta)}`
                    : Math.round(overallDelta)}
                </span>
              </div>

              {/* band change (only when it changed) */}
              {comp.band.delta !== 0 && (
                <div style={{ fontSize: 12, color: '#1A2A1F', marginBottom: 8 }}>
                  Band: <b>{comp.band.previous} → {comp.band.current}</b>
                </div>
              )}

              {/* improved / declined subject + topic chips */}
              {(improved.length > 0 || declined.length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {improved.map((m) => (
                    <span
                      key={m.key}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '3px 10px',
                        borderRadius: 999,
                        background: '#DCEFCC',
                        color: '#14593C',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      ▲ {m.label}{' '}
                      <span style={{ fontWeight: 500 }}>+{Math.round(m.delta)}</span>
                    </span>
                  ))}
                  {/* declined chips click through to targeted practice (ROO-160) */}
                  {declined.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={onPractiseSkill ? () => onPractiseSkill(m.key, m.label) : undefined}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '3px 10px',
                        borderRadius: 999,
                        border: 0,
                        background: '#FAD5C8',
                        color: '#B83E24',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: onPractiseSkill ? 'pointer' : 'default',
                      }}
                    >
                      ▼ {m.label}{' '}
                      <span style={{ fontWeight: 500 }}>{Math.round(m.delta)}</span>
                      {onPractiseSkill && <span style={{ fontWeight: 700 }}>· practise →</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Encouraging line */}
        <div
          style={{
            background: '#DCEFCC',
            border: '1.5px solid #5DAA3A',
            borderRadius: 12,
            padding: '12px 16px',
            fontSize: 13.5,
            fontWeight: 600,
            color: '#14593C',
            marginBottom: 20,
            lineHeight: 1.5,
          }}
        >
          {encouragingLine(result.scorePercent)}
        </div>

        {/* Skill radar (ROO-131) */}
        {radarAxes.length >= 3 && (
          <div
            style={{
              background: 'white',
              border: '1.5px solid #EFE6D0',
              borderRadius: 16,
              padding: '16px 18px 8px',
              marginBottom: 20,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#6B7A6F',
                textTransform: 'uppercase',
                letterSpacing: '0.10em',
                marginBottom: 6,
              }}
            >
              Skill Profile
            </div>
            <RadarChart axes={radarAxes} />
          </div>
        )}

        {/* Weak-area breakdown OR focus-area tags */}
        {hasBreakdown ? (
          <div
            style={{
              background: 'white',
              border: '1.5px solid #EFE6D0',
              borderRadius: 16,
              padding: '16px 18px',
              marginBottom: 20,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#6B7A6F',
                textTransform: 'uppercase',
                letterSpacing: '0.10em',
                marginBottom: 14,
              }}
            >
              Area Breakdown
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {result.breakdown!.map((item, idx) => {
                const tone = toneFor(idx);
                const pct = Math.round(item.percent);
                return (
                  <div key={item.area}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        marginBottom: 5,
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1A2A1F' }}>
                        {item.label || domainLabel(item.area)}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: tone.bar }}>
                        {pct}%
                        <span style={{ fontWeight: 400, color: '#6B7A6F', marginLeft: 4 }}>
                          ({item.correct}/{item.total})
                        </span>
                      </span>
                    </div>
                    <div
                      style={{
                        height: 8,
                        borderRadius: 999,
                        background: tone.bg,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${pct}%`,
                          borderRadius: 999,
                          background: tone.bar,
                        }}
                      />
                    </div>
                    {onPractiseSkill && pct < 80 && (
                      <button
                        type="button"
                        onClick={() => onPractiseSkill(item.area, item.label || domainLabel(item.area))}
                        style={{
                          marginTop: 6,
                          padding: '4px 12px',
                          borderRadius: 999,
                          border: '1.5px solid #5DAA3A',
                          background: 'white',
                          color: '#14593C',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        Practise {item.label || domainLabel(item.area)} →
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : focusLabels.length > 0 ? (
          <div
            style={{
              background: 'white',
              border: '1.5px solid #EFE6D0',
              borderRadius: 16,
              padding: '16px 18px',
              marginBottom: 20,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#6B7A6F',
                textTransform: 'uppercase',
                letterSpacing: '0.10em',
                marginBottom: 10,
              }}
            >
              Focus Areas
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {focusLabels.map((label) => (
                <span
                  key={label}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '6px 14px',
                    borderRadius: 999,
                    background: '#DCEFCC',
                    color: '#14593C',
                    fontSize: 13,
                    fontWeight: 700,
                    border: '1.5px solid #5DAA3A',
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {/* Recommended for you: subject → topic chips (ROO-156) */}
        {Array.isArray(result.recommendations) && result.recommendations.length > 0 && (
          <div
            style={{
              background: 'white',
              border: '1.5px solid #EFE6D0',
              borderRadius: 16,
              padding: '16px 18px',
              marginBottom: 20,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#6B7A6F',
                textTransform: 'uppercase',
                letterSpacing: '0.10em',
                marginBottom: 14,
              }}
            >
              Recommended for you
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {result.recommendations.map((rec) => {
                const showPractiseBtn = (rec.topics.length > 0 || rec.percent < 80) && !!onPractiseSkill;
                return (
                  <div key={rec.subject}>
                    {/* Subject header row */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: rec.topics.length > 0 ? 8 : 0,
                        flexWrap: 'wrap',
                        gap: 6,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#1A2A1F' }}>
                          {rec.subjectLabel}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#E26B4F' }}>
                          {Math.round(rec.percent)}%
                        </span>
                      </div>
                      {showPractiseBtn && (
                        <button
                          type="button"
                          onClick={() => onPractiseSkill!(rec.subject, rec.subjectLabel)}
                          style={{
                            padding: '4px 12px',
                            borderRadius: 999,
                            border: '1.5px solid #5DAA3A',
                            background: 'white',
                            color: '#14593C',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          Practise {rec.subjectLabel} →
                        </button>
                      )}
                    </div>
                    {/* Topic chips */}
                    {rec.topics.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {rec.topics.map((topic) =>
                          onPractiseSkill ? (
                            <button
                              key={topic.area}
                              type="button"
                              onClick={() => onPractiseSkill(topic.area, topic.label)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                padding: '5px 12px',
                                borderRadius: 999,
                                border: '1.5px solid #C5E0B0',
                                background: '#EFF7E8',
                                color: '#14593C',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              {topic.label}
                              <span style={{ opacity: 0.7, fontWeight: 500 }}>
                                {Math.round(topic.percent)}%
                              </span>
                            </button>
                          ) : (
                            <span
                              key={topic.area}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                padding: '5px 12px',
                                borderRadius: 999,
                                border: '1.5px solid #C5E0B0',
                                background: '#EFF7E8',
                                color: '#14593C',
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {topic.label}
                              <span style={{ opacity: 0.7, fontWeight: 500 }}>
                                {Math.round(topic.percent)}%
                              </span>
                            </span>
                          )
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* LLM insights + suggestions (ROO-131) */}
        <div style={{ marginBottom: 20 }}>
          <ReportInsights analysis={result.analysis} />
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: '1.5px solid #EFE6D0',
            paddingTop: 14,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 11, color: '#6B7A6F' }}>cleveroo.com</span>
          <span style={{ fontSize: 11, color: '#6B7A6F' }}>
            Personalised learning for every student
          </span>
        </div>
      </div>

      {/* ── ACTION BUTTONS (outside card ref — NOT captured in screenshot) ──
          One primary (Practise now, else Back), one secondary, and quiet
          text-links for Download/Share — four same-weight CTAs competed for
          attention before (ROO-220). */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          marginTop: 20,
          width: '100%',
          maxWidth: 540,
        }}
      >
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
          {onPractise && (
            <button
              type="button"
              onClick={onPractise}
              style={{
                flex: 1,
                minWidth: 150,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '12px 24px',
                borderRadius: 999,
                border: 0,
                background: '#14593C',
                fontFamily: "'Lexend', system-ui, sans-serif",
                fontSize: 14.5,
                fontWeight: 700,
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 6px 14px -6px rgba(20,89,60,.45)',
              }}
            >
              Practise now {ArrowRight}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              minWidth: 150,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '12px 24px',
              borderRadius: 999,
              ...(onPractise
                ? { border: '1.5px solid #E5D9BD', background: 'white', color: '#14593C' }
                : { border: 0, background: '#14593C', color: 'white', boxShadow: '0 6px 14px -6px rgba(20,89,60,.45)' }),
              fontFamily: "'Lexend', system-ui, sans-serif",
              fontSize: 14.5,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Back to dashboard {onPractise ? null : ArrowRight}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 22 }}>
          <button
            type="button"
            onClick={handleDownload}
            disabled={capturing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              border: 0,
              background: 'none',
              fontFamily: "'Lexend', system-ui, sans-serif",
              fontSize: 13,
              fontWeight: 600,
              color: '#6B7A6F',
              cursor: capturing ? 'default' : 'pointer',
              opacity: capturing ? 0.6 : 1,
              padding: 4,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {capturing ? 'Saving…' : 'Download report'}
          </button>
          <button
            type="button"
            onClick={handleShare}
            disabled={capturing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              border: 0,
              background: 'none',
              fontFamily: "'Lexend', system-ui, sans-serif",
              fontSize: 13,
              fontWeight: 600,
              color: '#6B7A6F',
              cursor: capturing ? 'default' : 'pointer',
              opacity: capturing ? 0.6 : 1,
              padding: 4,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            Share with family
          </button>
        </div>
      </div>

      {/* ── TOAST ── */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 28,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#1A2A1F',
            color: 'white',
            padding: '10px 20px',
            borderRadius: 999,
            fontSize: 13.5,
            fontWeight: 600,
            fontFamily: "'Lexend', system-ui, sans-serif",
            boxShadow: '0 8px 24px -8px rgba(20,42,31,.45)',
            zIndex: 9999,
            whiteSpace: 'nowrap',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
};

export default ScoreReport;
